(ns very.re-frame.core
  (:require
    [cljs.spec.alpha :as s]
    [reagent.core :as r]
    [re-frame.core :as rf]
    [re-frame.loggers :as log]
    [re-frame.std-interceptors :as in]
    [re-frame.interceptor :refer [->interceptor get-effect get-coeffect
                                  assoc-effect assoc-coeffect]]
    [runtime.core :refer [DEBUG]]
    [very.re-frame.event-log :as event-log]
    [very.re-frame.errors :as err])

  (:require-macros
    [logging.core :refer [log error]]
    [very.re-frame.core]))

(def *max-events-to-preserve* 50)

(defonce spec (atom nil))

(defn set-spec! [s]
  (reset! spec s))

(defn validate-db []
  (->interceptor
    :id :validate-db
    :after (fn [ctx]
             (let [db (get-in ctx [:effects :db])
                   untrimmed-event (get-in ctx [:coeffects :in/untrimmed-event])
                   event (get-in ctx [:coeffects :event])]
               (when (and (s/check-asserts?)
                          db
                          (not (nil? @spec))
                          (not (s/valid? @spec db)))
                 (log db)
                 (error (str "DB does not pass spec validation after
                                        event: \n" (or untrimmed-event event) "\n\n"
                                        ;(subs (s/explain @spec db) 0 1000)
                                        (s/explain-str @spec db)))))

             ctx)))

(defn- register [registered dsl]
  (assert (nil? registered) "You cannot register two prevent events at the same time. Unregister your previous preventer.")
  (assert (and (vector? (:and-instead-dispatch dsl))
               (= 1 (count (:and-instead-dispatch dsl)))) "and-instead-dispatch must be a single event to dispatch.")
  dsl)

(defn prevent-events
  "Take heed, young traveler, for here be dragons.

  Occasionally we have a need on a global level to prevent events from
  triggering handlers through the system. For example, when a user is filling
  out a multi-page form, we want to prevent them from accidentally or
  intentionally clicking on a top-level navigation button, which would cause
  them to lose all their progress on said form. Actually, we don't *really*
  wan't to *prevent* events, but we do want some mechanism for intercepting them
  and then decide what to do from there. In the case of the form example, we
  probably want to spit out a modal that warns the user their progress is about
  to be lost.

  Such a system should not be a giant global handler where we have to maintain a
  big list of events from disparate features. We should be able to determine, on
  a per-feature level, which events we are interested in preventing.

  Any events not specifically captured will be cancelled and instead the trigger
  another event where we will determine what to do at the feature level.

  In short, when a feature requiring prevent-events is initialized, it must
  register with 1) a unique key, 2) a set of events to capture 3) an event to
  dispatch when we detect an event belonging to the set of capture events.  When
  a feature is done preventing events, it must 1) unregister with the same
  unique key 2) decide whether or not to allow the most recent event which
  triggered the alternate dispatch to pass or to be gobbbled up after
  unregistering.

  {:register :unique-id
   :catch-these-events #{:a :b ...}
   :and-instead-dispatch [:handle-interruption-event]}

  {:unregister :unique-id
   :allow-last-event false}
  "
  []
  #_(atom {:register :unique-id
           :catch-these-events #{:event-a :event-b}
           :and-instead-dispatch [:single-event]
           :intercepted [:single-event]})
  (let [registered (atom nil)
        capture-event? (fn [[evt & rst]] (contains? (:catch-these-events @registered) evt))]
    (->interceptor
      :id :prevent-events
      :before (fn [ctx]
                ;; only catch events if we have some preventer registered
                (if-not @registered
                  ctx
                  (let [evt (get-in ctx [:coeffects :event])]
                    (if-not (capture-event? evt)
                      ctx
                      ;; when an event is captured, store it as the most
                      ;; recently intercepted event, but also strip the
                      ;; processing queue to prevent that event handler from
                      ;; being invoked entirely ... and instead queue up the
                      ;; and-instead-dispatch invocation for the next tick
                      (do
                        (log/console :log "Captured event:" evt
                                     "and instead dispatching" (:and-instead-dispatch @registered))
                        (swap! registered assoc :intercepted evt)
                        (-> ctx
                            (update :queue (fn [queue]
                                             (into #queue []
                                                   (remove
                                                     (fn [intc] (contains? #{:fx-handler :db-handler} (:id intc)))
                                                     queue))))
                            (update :effects assoc :dispatch (:and-instead-dispatch @registered))))))))

      :after (fn [ctx]
               (let [{fx :effects} ctx
                     dsl (:prevent-events fx)
                     reg (:register dsl)
                     unreg (:unregister dsl)]
                 ;; for all events, check to see if we're trying to register or
                 ;; unregister a prevent-events
                 (cond
                   ;; registering is easy - just store the fx
                   (some? reg) (do (swap! registered register dsl) ctx)
                   ;; when unregistering, assuming that :allow-last-event is
                   ;; true, dispatch the last intercepted event on the next
                   ;; tick
                   (some? unreg) (let [ctx (if (and (:allow-last-event dsl true) (:intercepted @registered))
                                             (do
                                               (log/console :log "Dispatching the previous captured event:" (:intercepted @registered))
                                               (assoc-in ctx [:effects :dispatch] (:intercepted @registered)))
                                             ctx)]
                                   (reset! registered nil)
                                   ctx)

                   ;; if we're not trying to reg/unreg, we have nothing to do
                   :else ctx))))))

(def *default-interceptors* [
                             (err/interceptor)
                             ;; (when DEBUG in/debug)
                             (when DEBUG (validate-db))
                             (event-log/interceptor *max-events-to-preserve*)
                             (prevent-events)
                             in/trim-v
                             ])

(defn enrich-fx [f]
  (->interceptor
    :id :enrich-fx
    :after (fn enrich-after-fx [context]
             (assoc context :effects (merge (:effects context)
                                            (f (:coeffects context)))))))

(defn reg-event-fx
  ([id f] (reg-event-fx id nil f))
  ([id interceptors f]
   (rf/reg-event-fx id [*default-interceptors* interceptors] f)))

(defn reg-event-db
  ([id f] (reg-event-db id nil f))
  ([id interceptors f]
   ;; @TODO is this too restrictive, always putting ours first?
   ;; note -- reframe already flattens when registering interceptors, so don't
   ;; worry about [default [provided]]
   (rf/reg-event-db id [*default-interceptors* interceptors] f)))

(defn reg-sub [& args]
  (apply rf/reg-sub args))

(defn <sub [& args]
  (deref (apply rf/subscribe args)))

(defn subscribe [& args]
  (apply rf/subscribe args))

(defn >evt [& args]
  (apply rf/dispatch args))

(defn >evt! [& args]
  (apply rf/dispatch-sync args))

(defn console [& args]
  (apply rf/console args))

(defn reg-fx [& args]
  (apply rf/reg-fx args))

(defn reg-cofx [& args]
  (apply rf/reg-cofx args))

(defn inject-cofx [& args]
  (apply rf/inject-cofx args))

(defn enrich [& args]
  (apply rf/enrich args))

(defn after [& args]
  (apply rf/after args))

(defn path [& args]
  (apply rf/path args))
